// src/components/Modals/TagResultsModal.jsx
import { useMemo } from "react";
import { ModalShell } from "../ModalShell";
import { ColoredSpan } from "../ColoredSpan";
import { useTasksQuery } from "../../api/queries/tasks";
import { useProjectsQuery } from "../../api/queries/projects";
import { useEmployeesQuery } from "../../api/queries/employees";
import { useRoleDelegationsQuery } from "../../api/queries/role-delegations";
import {
  TASK_STATUSES,
  PROJECT_STATUSES,
  PROJECT_STATUS_CONFIG,
} from "../../utils/constants";
import { fmtDMY } from "../../utils/date";
import { unwrapItems, MAX_REF_LIMIT } from "../../utils/dbShape";

export function TagResultsModal({
  tag,
  delegationId,
  openTask,
  openProject,
  onClose,
}) {
  const { data: tasksRes } = useTasksQuery({ limit: MAX_REF_LIMIT });
  const { data: projectsRes } = useProjectsQuery({ limit: MAX_REF_LIMIT });
  const { data: employeesRes } = useEmployeesQuery();
  const { data: delegationsRes } = useRoleDelegationsQuery();

  const tasks = useMemo(() => unwrapItems(tasksRes), [tasksRes]);
  const projects = useMemo(() => unwrapItems(projectsRes), [projectsRes]);
  const employees = useMemo(() => unwrapItems(employeesRes), [employeesRes]);
  const delegations = useMemo(
    () => unwrapItems(delegationsRes),
    [delegationsRes],
  );

  const delegation = useMemo(() => {
    if (!delegationId) return null;
    return delegations.find((d) => d.id === delegationId);
  }, [delegations, delegationId]);

  const matchedTasks = useMemo(() => {
    if (delegation) {
      const ids = delegation.delegatedTaskIds || [];
      return tasks.filter((t) => ids.includes(t.id));
    }
    if (tag) {
      return tasks.filter(
        (t) =>
          t.status !== "draft" && Array.isArray(t.tags) && t.tags.includes(tag),
      );
    }
    return [];
  }, [tasks, tag, delegation]);

  const matchedProjects = useMemo(() => {
    if (delegation) return []; // Проекты в этом режиме не показываем
    if (tag) {
      return projects.filter(
        (p) =>
          p.status !== "draft" && Array.isArray(p.tags) && p.tags.includes(tag),
      );
    }
    return [];
  }, [projects, tag, delegation]);

  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.last} ${e.first}` : "-";
  };

  const title = delegationId ? "Переданные задачи" : `Метка: ${tag}`;

  return (
    <ModalShell title={title} onClose={onClose} width={720} showSave={false}>
      <div className="rep-panel p-4">
        <div className="rep-panel-title">Задачи ({matchedTasks.length})</div>
        {matchedTasks.length === 0 ? (
          <div className="empty-note p-4">Ничего не найдено</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Задача</th>
                <th>Проект</th>
                <th>Исполнитель</th>
                <th>Статус</th>
                <th>Срок</th>
              </tr>
            </thead>
            <tbody>
              {matchedTasks.map((t) => {
                const p = projects.find((x) => x.id === t.projectId);
                const statusDef = TASK_STATUSES[t.status] || {
                  label: t.status || "-",
                  color: "#64748b",
                };
                return (
                  <tr
                    key={t.id}
                    className="clickable-row"
                    onClick={() => openTask(t.id)}
                  >
                    <td>
                      <b>{t.title}</b>
                    </td>
                    <td>{p?.code || "-"}</td>
                    <td>{t.assigneeId ? empName(t.assigneeId) : "-"}</td>
                    <td>
                      <ColoredSpan
                        color={statusDef.color}
                        className="st-chip st-chip--tinted"
                      >
                        {statusDef.label}
                      </ColoredSpan>
                    </td>
                    <td>{t.deadline ? fmtDMY(t.deadline) : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!delegationId && (
        <div className="rep-panel p-4 mt-3">
          <div className="rep-panel-title">
            Проекты ({matchedProjects.length})
          </div>
          {matchedProjects.length === 0 ? (
            <div className="empty-note p-4">Ничего не найдено</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Проект</th>
                  <th>Ответственный</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {matchedProjects.map((p) => {
                  const statusDef = PROJECT_STATUS_CONFIG[p.status] || {
                    label: PROJECT_STATUSES[p.status] || p.status || "-",
                    color: "#64748b",
                  };
                  return (
                    <tr
                      key={p.id}
                      className="clickable-row"
                      onClick={() => openProject(p.id)}
                    >
                      <td>
                        <b>{p.code}</b>
                      </td>
                      <td>{p.name}</td>
                      <td>{p.managerId ? empName(p.managerId) : "-"}</td>
                      <td>
                        <ColoredSpan
                          color={statusDef.color}
                          className="st-chip st-chip--tinted"
                        >
                          {statusDef.label}
                        </ColoredSpan>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </ModalShell>
  );
}
